import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["content", "icon"]
  static values = {
    overflow: Boolean,
    startCollapsed: { type: Boolean, default: false },
    desktopBreakpoint: { type: Number, default: 1024 },
    sectionId: { type: String, default: "" },
    storageKey: { type: String, default: "filter-collapse-state" }
  }

  connect() {
    this._initializing = true
    const savedState = this._loadState()

    if (savedState !== null) {
      // Restore persisted state from localStorage
      if (savedState === "collapsed") {
        this.collapse()
      } else {
        this.expand()
      }
    } else if (this.startCollapsedValue && window.matchMedia(`(min-width: ${this.desktopBreakpointValue}px)`).matches) {
      this.collapse()
    } else if (this.hasContentTarget) {
      this.expand()
    }
    this._initializing = false
  }

  disconnect() {
    clearTimeout(this._persistTimer)
  }

  toggle() {
    if (this.isExpanded()) {
      this.collapse()
    } else {
      this.expand()
    }
  }

  // Public API used by other controllers
  isExpanded() {
    return this.hasContentTarget && !this.contentTarget.classList.contains("max-h-0")
  }

  expand() {
    if (!this.hasContentTarget) return
    this.contentTarget.classList.remove("max-h-0", "mb-0")
    this.contentTarget.classList.add("max-h-[2000px]")
    
    if (this.overflowValue) {
      setTimeout(() => {
        if (this.isExpanded()) {
          this.contentTarget.classList.remove("overflow-hidden")
        }
      }, 300)
    }

    if (this.hasIconTarget) this.iconTarget.textContent = "-"
    this._persistState()
    this.dispatch("change")
  }

  collapse() {
    if (!this.hasContentTarget) return
    
    if (this.overflowValue) {
      this.contentTarget.classList.add("overflow-hidden")
    }

    this.contentTarget.classList.remove("max-h-[2000px]")
    this.contentTarget.classList.add("max-h-0", "mb-0")
    if (this.hasIconTarget) this.iconTarget.textContent = "+"
    this._persistState()
    this.dispatch("change")
  }

  _persistState() {
    if (this._initializing || !this.sectionIdValue) return
    clearTimeout(this._persistTimer)
    this._persistTimer = setTimeout(() => {
      try {
        const states = JSON.parse(localStorage.getItem(this.storageKeyValue) || "{}")
        states[this.sectionIdValue] = this.isExpanded() ? "expanded" : "collapsed"
        localStorage.setItem(this.storageKeyValue, JSON.stringify(states))
      } catch (e) {
        console.warn("[toggle] Failed to persist state:", e)
      }
    }, 100)
  }

  // Private: load persisted state from localStorage
  _loadState() {
    if (!this.sectionIdValue) return null
    try {
      const states = JSON.parse(localStorage.getItem(this.storageKeyValue) || "{}")
      return states[this.sectionIdValue] || null
    } catch (e) {
      return null
    }
  }
}